import { useTranslation } from 'react-i18next';
import { CheckIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import { ExportStatusIcon } from 'widgets/home/ExportStatusIcon';
import { ExportStatusValue } from 'types/result';
import {
  ChevronDownIcon,
  DropdownItemCheckWrapper,
  DropdownItemText,
  FilterDropdownActiveLabel,
  FilterDropdownLabel,
  FilterDropdownTrigger,
  TimeRangeDropdownItem,
} from './ExportStatusFilter.styles';

interface ExportStatusFilterProps {
  exportStatusFilter: ExportStatusValue | undefined;
  onToggleExportStatusFilter: (status: ExportStatusValue | undefined) => void;
}

const exportStatusLabelKeyMap: Record<string, string> = {
  [ExportStatusValue.NotExported]: 'notExported',
  [ExportStatusValue.Failed]: 'failed',
  [ExportStatusValue.NoStatusFilter]: 'exportNotStarted',
  [ExportStatusValue.NoStatus]: 'exportNotStarted',
  [ExportStatusValue.Processing]: 'processing',
};

export const ExportStatusFilter = ({
  exportStatusFilter,
  onToggleExportStatusFilter,
}: ExportStatusFilterProps) => {
  const { t } = useTranslation();

  const labelKey =
    exportStatusFilter === undefined
      ? 'all'
      : (exportStatusLabelKeyMap[exportStatusFilter] ?? exportStatusFilter.toLowerCase());

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterDropdownTrigger>
          <div className="flex items-center gap-2 overflow-hidden">
            {exportStatusFilter ? (
              <>
                <ExportStatusIcon
                  animate={false}
                  status={
                    exportStatusFilter === ExportStatusValue.NoStatusFilter
                      ? undefined
                      : (exportStatusFilter.split(',')[0] as ExportStatusValue)
                  }
                />
                <FilterDropdownActiveLabel className="truncate">
                  {t(`home.exportStatusLabels.${labelKey}`)}
                </FilterDropdownActiveLabel>
              </>
            ) : (
              <FilterDropdownLabel>{t('home.exportStatusLabels.all')}</FilterDropdownLabel>
            )}
          </div>
          <ChevronDownIcon />
        </FilterDropdownTrigger>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <TimeRangeDropdownItem
          $isSelected={exportStatusFilter === ExportStatusValue.NoStatusFilter}
          onClick={() => onToggleExportStatusFilter(ExportStatusValue.NoStatusFilter)}
        >
          <DropdownItemText>
            <div className="flex items-center gap-2">
              <ExportStatusIcon status={undefined} />
              {t('home.exportStatusLabels.exportNotStarted')}
            </div>
          </DropdownItemText>
          <DropdownItemCheckWrapper>
            {exportStatusFilter === ExportStatusValue.NoStatusFilter && (
              <CheckIcon className="w-4 h-4" />
            )}
          </DropdownItemCheckWrapper>
        </TimeRangeDropdownItem>
        <TimeRangeDropdownItem
          $isSelected={exportStatusFilter === ExportStatusValue.Processing}
          onClick={() => onToggleExportStatusFilter(ExportStatusValue.Processing)}
        >
          <DropdownItemText>
            <div className="flex items-center gap-2">
              <ExportStatusIcon status={ExportStatusValue.Progress} animate={false} />
              {t('home.exportStatusLabels.processing')}
            </div>
          </DropdownItemText>
          <DropdownItemCheckWrapper>
            {exportStatusFilter === ExportStatusValue.Processing && (
              <CheckIcon className="w-4 h-4" />
            )}
          </DropdownItemCheckWrapper>
        </TimeRangeDropdownItem>
        <TimeRangeDropdownItem
          $isSelected={exportStatusFilter === ExportStatusValue.Failed}
          onClick={() => onToggleExportStatusFilter(ExportStatusValue.Failed)}
        >
          <DropdownItemText>
            <div className="flex items-center gap-2">
              <ExportStatusIcon status={ExportStatusValue.Failure} />
              {t('home.exportStatusLabels.failed')}
            </div>
          </DropdownItemText>
          <DropdownItemCheckWrapper>
            {exportStatusFilter === ExportStatusValue.Failed && <CheckIcon className="w-4 h-4" />}
          </DropdownItemCheckWrapper>
        </TimeRangeDropdownItem>
        <TimeRangeDropdownItem
          $isSelected={exportStatusFilter === ExportStatusValue.Success}
          onClick={() => onToggleExportStatusFilter(ExportStatusValue.Success)}
        >
          <DropdownItemText>
            <div className="flex items-center gap-2">
              <ExportStatusIcon status={ExportStatusValue.Success} />
              {t('home.exportStatusLabels.success')}
            </div>
          </DropdownItemText>
          <DropdownItemCheckWrapper>
            {exportStatusFilter === ExportStatusValue.Success && <CheckIcon className="w-4 h-4" />}
          </DropdownItemCheckWrapper>
        </TimeRangeDropdownItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
